import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { RootState } from '../../../../store/root-reducer';
import { LeadFinderPrimaryTab, LeadFinderSecondaryTab } from '../../types';
import {
  setActiveLeadType,
  setActiveSubTab,
  setShowAiSearchPopup,
  setBulkPeopleModeActive,
} from '../../v2-lead-slice';
import { updateLeadFinderVersionRequest } from '../../../home/extra-actions';
import { leadFinderViewOnly } from '../../../../shared/utils/user-details';
import { userOsIdentifier } from '../ai-search-bar/helper';

let hasCalledUpdateLeadFinderVersionOnLoad = false;

const isPrimaryTab = (value?: string): value is LeadFinderPrimaryTab =>
  !!value &&
  Object.values(LeadFinderPrimaryTab).includes(value as LeadFinderPrimaryTab);

const isSecondaryTab = (value?: string): value is LeadFinderSecondaryTab =>
  !!value &&
  Object.values(LeadFinderSecondaryTab).includes(
    value as LeadFinderSecondaryTab,
  );

interface UseLeadFinderEffectsProps {
  leadFinder: any;
  setShowWhatsNewModal: (show: boolean) => void;
  shouldBlockAccess: boolean;
  preservedFiltersRef: React.MutableRefObject<{
    saved: string;
    totalNew: string;
  }>;
  tabSpecificFiltersRef: React.MutableRefObject<{
    people: string;
    company: string;
  }>;
  skipUrlPushForBulkSearchRef: React.MutableRefObject<boolean>;
  tabSpecificBulkModeRef: React.MutableRefObject<{
    people: boolean;
    company: boolean;
  }>;
  bulkPeopleSearch: {
    isPending: boolean;
    filters: string;
    isBulkModeActive: boolean;
  };
  fetchLeadTags: (applyLoading?: boolean) => void;
  keywordInputRef: React.RefObject<HTMLInputElement>;
  tab?: string;
  subtab?: string;
}

export const useLeadFinderEffects = (
  props: UseLeadFinderEffectsProps,
): void => {
  const {
    leadFinder,
    setShowWhatsNewModal,
    shouldBlockAccess,
    preservedFiltersRef,
    tabSpecificFiltersRef,
    skipUrlPushForBulkSearchRef,
    tabSpecificBulkModeRef,
    bulkPeopleSearch,
    fetchLeadTags,
    tab,
    subtab,
  } = props;

  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { activeLeadType, activeSubTab, modals } = useSelector(
    (state: RootState) => state.leadFinderV2,
  );
  const { showBulkRevealTagModal } = modals;

  // Effect 1: Lead finder version check
  useEffect(() => {
    if (
      location.pathname !== '/v2/leads/people/total' ||
      hasCalledUpdateLeadFinderVersionOnLoad ||
      leadFinder.isVersionLocked
    ) {
      return;
    }
    const version = leadFinder?.version;
    if (version === 'v1') {
      hasCalledUpdateLeadFinderVersionOnLoad = true;
      dispatch(updateLeadFinderVersionRequest(version));
    }
  }, [location.pathname, leadFinder?.version, dispatch]);

  // Effect 2: showWhatsNew URL param handling
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('showWhatsNew') === 'true') {
      setShowWhatsNewModal(true);
      // Remove the param from URL to avoid showing modal on refresh
      params.delete('showWhatsNew');
      const newSearch = params.toString();
      history.replace({
        pathname: location.pathname,
        search: newSearch ? `?${newSearch}` : '',
      });
    }
  }, [location.search, location.pathname, history]);

  // Effect 3: Access guard redirect
  useEffect(() => {
    if (shouldBlockAccess) {
      history.replace('/leads');
    }
  }, [shouldBlockAccess, history]);

  // Effect 4: Clear preserved filters when URL search is cleared (excluding pagination)
  useEffect(() => {
    if (subtab !== LeadFinderSecondaryTab.SAVED) {
      const params = new URLSearchParams(location.search);
      params.delete('page');
      params.delete('start');
      params.delete('take');
      // If URL has no filter params, clear preserved filters for current tab
      if (params.toString().length === 0) {
        const currentTabKey =
          tab === LeadFinderPrimaryTab.PEOPLE ? 'people' : 'company';
        const hasPreservedTabFilters =
          (tabSpecificFiltersRef.current?.[currentTabKey] || '').trim().length >
          0;
        const isAnyBulkSearchModeActive =
          skipUrlPushForBulkSearchRef.current ||
          (currentTabKey === 'people'
            ? tabSpecificBulkModeRef.current.people
            : tabSpecificBulkModeRef.current.company) ||
          bulkPeopleSearch.isPending ||
          bulkPeopleSearch.isBulkModeActive;
        if (!isAnyBulkSearchModeActive && !hasPreservedTabFilters) {
          preservedFiltersRef.current.totalNew = '';
          tabSpecificFiltersRef.current[currentTabKey] = '';
          if (currentTabKey === 'people') {
            dispatch(setBulkPeopleModeActive(false));
          }
        }
      }
    }
  }, [
    location.search,
    subtab,
    tab,
    dispatch,
    skipUrlPushForBulkSearchRef,
    tabSpecificBulkModeRef,
    bulkPeopleSearch.isPending,
    bulkPeopleSearch.isBulkModeActive,
  ]);

  // Effect 5: Tab normalization
  useEffect(() => {
    const normalizedLeadType = isPrimaryTab(tab)
      ? tab
      : LeadFinderPrimaryTab.PEOPLE;
    const normalizedSubTab = isSecondaryTab(subtab)
      ? subtab
      : LeadFinderSecondaryTab.TOTAL;

    if (normalizedLeadType !== activeLeadType) {
      dispatch(setActiveLeadType(normalizedLeadType));
    }
    if (normalizedSubTab !== activeSubTab) {
      dispatch(setActiveSubTab(normalizedSubTab));
    }

    const currentPath = `/v2/leads/${normalizedLeadType}/${normalizedSubTab}`;
    const expectedPath = `/v2/leads/${tab || LeadFinderPrimaryTab.PEOPLE}/${
      subtab || LeadFinderSecondaryTab.TOTAL
    }`;

    if (currentPath !== expectedPath) {
      history.replace(currentPath + location.search);
    }
  }, [
    tab,
    subtab,
    activeLeadType,
    activeSubTab,
    dispatch,
    history,
    location.search,
  ]);

  // Effect 6: Lead tags fetch on saved tab
  useEffect(() => {
    if (
      shouldBlockAccess ||
      activeSubTab !== LeadFinderSecondaryTab.SAVED ||
      leadFinderViewOnly()
    ) {
      return;
    }
    fetchLeadTags(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSubTab, shouldBlockAccess]);

  // Effect 7: Lead tags fetch on bulk reveal modal open
  useEffect(() => {
    if (shouldBlockAccess || !showBulkRevealTagModal) {
      return;
    }
    fetchLeadTags(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showBulkRevealTagModal, shouldBlockAccess]);

  // Effect 8: Keyboard shortcut for AI search
  useEffect(() => {
    if (activeSubTab === LeadFinderSecondaryTab.SAVED) return;

    const handleKeyDown = (e) => {
      const platform = userOsIdentifier();
      const isMac = platform.includes('mac');

      if (
        (isMac && e.metaKey && e.key.toLowerCase() === 'k') ||
        (!isMac && e.ctrlKey && e.key.toLowerCase() === 'k')
      ) {
        e.preventDefault();
        dispatch(setShowAiSearchPopup(true));
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeSubTab]);
};
